// Converts the old flat CMS records (one set of keys per page, no `sections`)
// into page-builder sections when a page is opened in the builder. Same
// mapping as the customer site's lib/cmsLegacy.js — keep the two in sync.

const html = (value) =>
  value && typeof value === "object" ? value.description || value.title || "" : value || "";

const list = (value) => (Array.isArray(value) ? value : []);

const section = (type, data) => ({ id: `legacy-${type}`, type, enabled: true, data });

const policy = (d, itemsKey, extra = {}) =>
  section("PolicySections", {
    title: d.title || "",
    intro: "",
    items: list(d[itemsKey]).map((i) => ({
      heading: i?.name || "",
      content: i?.description || "",
    })),
    show_list_markers: false,
    large_mobile_spacing: false,
    ...extra,
  });

const heroBanner = (d) =>
  section("HeroBanner", {
    title: d.banner_title || "",
    description: html(d.banner_description),
    background_images: list(d.banner_image),
  });

const affiliations = (d) =>
  section("Affiliations", {
    title: d.affiliation_title || "",
    logos: list(d.affiliations).map((a) => ({ image: list(a?.affiliation_image) })),
  });

const imageTextBlock = (title, description, image) =>
  section("ImageTextBlock", {
    title: title || "",
    description: html(description),
    image: list(image),
  });

const whyChooseUs = (d, showDescription) =>
  section("WhyChooseUs", {
    title: d.why_you_choose_us_section_title || "",
    show_description: showDescription,
    items: list(d.why_you_choose).map((i) => ({
      name: i?.name || "",
      description: html(i?.description),
      image: list(i?.image),
    })),
  });

const verifiedInstaller = (d, authorized) =>
  section("VerifiedInstaller", {
    title: d.verified_installer?.verified_installer_title || "",
    description: d.verified_installer?.verified_installer_description || "",
    points_title: d.with_verified_installation?.with_verified_installation_title || "",
    points: list(d.with_verified_installation?.with_verified_installation_point).map((p) => ({
      text: p?.verified_installation_point || "",
    })),
    trust_title: authorized ? d.why_customers_trust?.title || "" : "",
    trust_points: authorized
      ? list(d.why_customers_trust?.points).map((p) => ({ text: p?.point || "" }))
      : [],
    results_title: authorized ? d.customer_results?.title || "" : "",
    results: authorized
      ? list(d.customer_results?.testimonials).map((t) => ({
          quote: t?.quote || "",
          author: t?.author || "",
        }))
      : [],
    sticky_layout: authorized,
  });

const verifiedLocations = (d, authorized) =>
  section("VerifiedLocations", {
    title: d.location_title || "",
    description: authorized ? d.location_description || "" : "",
    locations: list(d.verified_location).map((l) => ({
      title: l?.location_title || "",
      address: l?.location_address || "",
      email: l?.email || "",
      phone: l?.phone_number || "",
      website: l?.website_url || "",
      lat: l?.lat,
      lng: l?.lng,
    })),
  });

const LEGACY_PAGES = {
  home: (d) => [
    heroBanner(d),
    section("ServicesGrid", {
      title: d.services_title || "",
      services: list(d.services).map((s) => ({
        name: s?.service_name || "",
        image: list(s?.service_image),
        link: s?.redirect_path || "",
      })),
    }),
    affiliations(d),
    section("VideoCta", {
      title: d.youtube_main_title || "",
      subtitle: d.youtube_sub_title || "",
      video_url: d.youtube_link?.redirect_path || "",
    }),
    section("TrustHighlights", {
      title: d.trust_our_work_title || "",
      items: list(d.our_works).map((w) => ({
        name: w?.work_name || "",
        image: list(w?.work_image),
      })),
    }),
    section("ActionCards", {
      wholesalers: {
        title: d.wholeSalers_section_title || "",
        description: html(d.wholeSalers_section_description),
        button_text: d.wholesalers_button_text || "",
        button_link: d.wholesalers_button_link || "",
        benefits: list(d.wholesalers_benefits).map((b) => ({
          name: b?.benefit_name || "",
          image: list(b?.benefit_image),
        })),
      },
      build: {
        title: html(d.jeep_build_section_title),
        benefits: list(d.jeep_build_benefits).map((b) => ({ name: b?.benefit_name || "" })),
        image: list(d.jeep_build_section_image),
      },
    }),
    imageTextBlock(d.body_banner_title, d.body_banner_description, d.body_banner_image),
    section("Testimonials", {
      title: d.testimonial_section_title || "",
      testimonials: list(d.testimonials).map((t) => ({
        name: t?.customer_name || "",
        image: list(t?.customer_image),
        date: t?.testimonial_date || null,
        rating: t?.customer_rating ?? "",
        review: t?.customer_testimonial || "",
      })),
    }),
  ],

  "about-us": (d) => [
    section("PageBanner", {
      outline_heading: "About Us",
      title: d.banner_title || "",
      description: html(d.banner_description),
      image: list(d.banner_image),
    }),
    affiliations(d),
    imageTextBlock(d.banner2_title, d.banner2_description, d.banner2_image),
    section("YoutubeVideo", { title: "About Us", video_url: d.youtube_link?.redirect_path || "" }),
    whyChooseUs(d, true),
    section("ImageScroller", {
      title: d.banner3_title || "",
      description: html(d.banner3_description),
      images: list(d.banner3_images).map((i) => ({ image: list(i?.image) })),
    }),
    section("CommunityBanner", {
      title: d.body_banner_title || "",
      description: html(d.body_banner_description),
      image: list(d.body_banner_image),
    }),
    section("OurLocations", {
      title: "Our Location",
      locations: list(d.about_locations).map((l) => ({
        title: l?.title || "",
        sub_title: l?.sub_title || "",
        image: list(l?.location_img),
      })),
    }),
  ],

  "contact-us": (d) => [
    section("ContactDetails", {
      title: d.main_title || "",
      sales_heading: "Advice, Sales & Support",
      sales_toll_free: d.advice_sales_support?.sales_toll_free_number || "",
      sales_toll_free_tel: "tel:+18556366287",
      sales_local: d.advice_sales_support?.us_local_number || "",
      sales_local_tel: "tel:+14079775100",
      button_text: d.talk_to_live?.name || "",
      button_link: d.talk_to_live?.redirect_path || "",
      wholesale_heading: "Wholesale",
      wholesale_toll_free: d.wholesale?.toll_free_number || "",
      wholesale_toll_free_tel: "tel:+18008364858",
    }),
    section("LocationsMap", {
      locations: list(d.contact_locations_details).map((l) => ({
        name: l?.name || "",
        subtitle: l?.subtitle || "",
        image: list(l?.location_image),
      })),
    }),
  ],

  "for-wholesalers": (d) => [
    section("WholesalersBanner", {
      outline_heading: "“Become a Wholesaler”",
      title: d.for_wholesaler_title || "",
      description: d.for_wholesaler_description || "",
      image: list(d.for_wholesaler_image),
    }),
    section("OemSolutions", {
      title: d.oem_infotainment_solutions_title || "",
      description: d.oem_infotainment_solutions_description || "",
      items: list(d.oem_info_solutions).map((i) => ({
        name: i?.name || "",
        image: list(i?.image),
      })),
    }),
    whyChooseUs(d, false),
    section("SalesContact", {
      title: d.seller_title || "",
      image: list(d.seller_image),
      name: d.seller_address || "",
      department: d.seller_department || "",
      description: d.seller_description || "",
      phone: d.seller_number || "",
      email: d.seller_email || "",
      team: list(d.salesman).map((s) => ({
        name: s?.salesman_name || "",
        info: s?.salesman_info || "",
        rating: s?.salesman_rating ?? 0,
        review: s?.salesman_review || "",
        image: list(s?.salesman_image),
      })),
    }),
    section("WholesaleAccount", {
      title: d.get_wholesale_account_title || "",
      description: d.get_wholesale_account_description || "",
      image: list(d.wholesale_image),
      button_text: "Get A Wholesale Account",
      form_url: "https://survey.zohopublic.com/zs/7HCOiA",
    }),
  ],

  "verified-installer": (d) => [verifiedInstaller(d, false), verifiedLocations(d, false)],

  "authorized-car-radio-repair-near-me": (d) =>
    [
      verifiedInstaller(d, true),
      verifiedLocations(d, true),
      d.extra_details ? section("RichText", { content: d.extra_details }) : null,
      d.faq_section?.title || list(d.faq_section?.faqs).length
        ? section("FaqAccordion", {
            title: d.faq_section?.title || "",
            faqs: list(d.faq_section?.faqs).map((f) => ({
              question: f?.question || "",
              answer: f?.answer || "",
            })),
          })
        : null,
    ].filter(Boolean),

  "privacy-policy": (d) => [policy(d, "privacypolicys")],
  "warranty-terms": (d) => [policy(d, "warrantyterm")],
  "licensing-compliance": (d) => [policy(d, "licensingcompliance")],
  "core-refund-policy": (d) => [
    policy(d, "corerefundpolicy", { show_list_markers: true, large_mobile_spacing: true }),
  ],
  "term-and-condition": (d) => [policy(d, "term&condtions", { intro: d.description || "" })],
  "shipping-information": (d) => [
    section("PolicySections", {
      title: d.title || "",
      intro: d.description || "",
      items: [],
      show_list_markers: true,
      large_mobile_spacing: false,
    }),
  ],
};

// Blog posts are saved flat (see EditCMSPage save); the editor works on the
// BlogDetail form's keys.
const blogDetail = (d) => [
  section("BlogDetail", {
    title: d.title || "",
    heading: d.content?.heading || d.heading || "",
    content: d.content?.description || (typeof d.content === "string" ? d.content : ""),
    excerpt: d.excerpt?.description || (typeof d.excerpt === "string" ? d.excerpt : ""),
    feature_image: d.feature_image?.[0]?.url || d.feature_image,
    author: d.author || "Infotainment Specialist",
    category: d.blog_category_id || d.category || "Repair Guides",
    tags: d.tags || [],
    visibility_date: d.visibility_date || null,
  }),
];

// Older builder type names → current section types.
const TYPE_ALIASES = { header: "Header", footer: "Footer", blog_detail: "BlogDetail" };

export function normalizeSectionTypes(sections) {
  return sections.map((s) => (TYPE_ALIASES[s?.type] ? { ...s, type: TYPE_ALIASES[s.type] } : s));
}

export function legacyDataToSections(data, { slug, group } = {}) {
  if (!data || typeof data !== "object") return [];
  if (String(group || "").toLowerCase() === "blogs") return blogDetail(data);
  const convert = LEGACY_PAGES[String(slug || "").toLowerCase()];
  return convert ? convert(data) : [];
}
